import {useEffect, useMemo} from 'react';

import {SelectPickerGeneric} from 'shells/select-picker-generic/select-picker-generic';

import type {GetSessionUser_loggedInUser_sendableEmailAddresses as UserSendableEmailAddress} from 'nutshell-graphql-types';

import {useGetSharedInboxConfig} from '../../../../../../../ui/inbox/email/graphql/hooks/use-get-shared-inbox-config';

import {INTERNAL_NOTE_OPTION, INTERNAL_NOTE_OPTION_VALUE} from './consts';
import {
    mergeAndFlattenSenderAddresses,
    getSenderAddressOptions,
    getSenderAddressStyles,
} from './helpers';
import {MODE, type Mode, type SenderAddress, type SenderAddressOption} from './types';

import styles from './inbox-rich-text-input.module.css';

interface Props {
    selectedSender: SenderAddress | null;
    setSelectedSender: (sender: SenderAddress | null) => void;
    mode: Mode;
    setMode: (mode: Mode) => void;
    clearAllImages: () => void;
    userSendableEmailAddresses?: UserSendableEmailAddress[] | null | undefined;
}

export const EmailInputHeader = (props: Props) => {
    const {mode, selectedSender, setSelectedSender} = props;

    const {sharedInboxConfig} = useGetSharedInboxConfig();

    const senderAddresses = useMemo(() => {
        return mergeAndFlattenSenderAddresses(
            props.userSendableEmailAddresses,
            sharedInboxConfig?.sharedAddresses
        );
    }, [props.userSendableEmailAddresses, sharedInboxConfig?.sharedAddresses]);

    useEffect(() => {
        if (mode === MODE.EMAIL && !selectedSender) {
            setSelectedSender(senderAddresses.find((address) => !address.isDisabled) ?? null);
        }
    }, [mode, selectedSender, senderAddresses, setSelectedSender]);

    const options = getSenderAddressOptions(senderAddresses);

    return (
        <div className={styles['selector-header']}>
            <SelectPickerGeneric
                options={options}
                placeholder='Send as…'
                menuPlacement='top'
                menuWidth={250}
                menuZIndex={10001}
                isOptionDisabled={(option: SenderAddressOption) => option.isDisabled}
                noBorder={true}
                styles={{...getSenderAddressStyles(props.mode)}}
                value={
                    props.mode === MODE.INTERNAL_NOTE
                        ? INTERNAL_NOTE_OPTION
                        : options.find((option) => option.value === props.selectedSender?.address)
                }
                onChange={(option: SenderAddressOption) => {
                    if (option.value === INTERNAL_NOTE_OPTION_VALUE) {
                        props.setMode(MODE.INTERNAL_NOTE);
                        props.setSelectedSender(null);
                        props.clearAllImages();
                    } else {
                        props.setMode(MODE.EMAIL);
                        props.setSelectedSender(
                            senderAddresses.find((item) => item.address === option.value) ?? null
                        );
                    }
                }}
            />
        </div>
    );
};
